import { useCallback, useState, useEffect, useRef } from 'react';

import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { toast } from 'sonner';

import { urlValidationEndpoints } from '@/api/endpoints';
import { callGet, callPost } from '@/api/fetcher';
import {
  getCountryCodeFromLanguage,
  TemplateFlagIcon,
} from '@/components/atoms/FlagEmoji/TemplateFlagIcon';
import {
  SidebarWrapperContent,
  SidebarWrapperFooter,
} from '@/components/atoms/SidebarWrapper/SidebarWrapper';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  FormField,
  FormControl,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import useCollections from '@/hooks/useCollections';
import useDatasources from '@/hooks/useDatasources';
import { cn } from '@/lib/utils';
import { openPublishModal } from '@/redux/collections/actions';
import { getPermissions } from '@/redux/permissions/selectors';
import { Datasource, DatasourceType } from '@/shared/types/collections';
import { OptionBase } from '@/shared/types/common';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { encodeBase64, extractNames, generateUniqueName, noop } from '@/util/util';

import {
  EXTERNAL_SOURCE_CATEGORIES,
  EXTERNAL_SOURCE_FIELD,
  EXTERNAL_SOURCE_FOOTER,
  EXTERNAL_SOURCE_SECTION,
} from './layout';
import { useExternalSourceDatasourceId } from './useExternalSourceDatasourceId';
import { isZendeskConfig } from '../../../helper';

export const ZENDESK_EMAIL = 'test@email.com' as const;
export const ZENDESK_ACCESS_TOKEN = '1234567890' as const;

interface LocaleArticleCount {
  locale: string;
  count: number;
}

interface Category {
  id: string;
  name: string;
  description: string;
  position: number;
  locale: string;
}

type ZendeskFormProps = { handleCancel: (withValidation?: boolean) => void };

const CATEGORY_SKELETON_WIDTHS = ['w-40', 'w-56', 'w-32', 'w-48', 'w-36'];

const CategorySkeleton = () => {
  return (
    <div className="w-full space-y-2 py-1 pl-2">
      <div className="flex items-center space-x-2">
        <Skeleton className="size-4 shrink-0 rounded-[4px]" />
        <Skeleton className="h-4 w-24" />
      </div>
      <Separator />
      {CATEGORY_SKELETON_WIDTHS.map((width) => (
        <div key={width} className="my-4 flex items-center space-x-2">
          <Skeleton className="size-4 shrink-0 rounded-[4px]" />
          <Skeleton className={cn('h-4 max-w-full', width)} />
        </div>
      ))}
    </div>
  );
};

export const ZendeskForm = ({ handleCancel }: ZendeskFormProps) => {
  // Redux selectors
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'collections', actions.WRITE)
  );
  const dispatch = useDispatch();
  // Initial mount ref to prevent multiple data fetches
  const initializedRef = useRef(false);

  // Hooks
  const { collectionId } = useParams();
  const { collection } = useCollections(collectionId);
  const form = useFormContext();
  const { t } = useTranslation();
  // URL first: redux only applies on the list page, where the URL has no datasource id.
  const datasourceId = useExternalSourceDatasourceId();
  const { datasource, getStatus, createDatasource, updateDatasource, datasources } = useDatasources(
    collectionId,
    datasourceId
  );

  const isZendeskEditing =
    !!datasourceId && getStatus === 'success' && datasource.type === 'zendesk_kb';

  // Local state
  const [zendeskLanguages, setZendeskLanguages] = useState<OptionBase[]>([]);
  const [zendeskCategories, setZendeskCategories] = useState<Category[]>([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [isLanguageLoading, setIsLanguageLoading] = useState(false);
  const [isCategoriesLoading, setIsCategoriesLoading] = useState(false);
  const [isEditInitialized, setIsEditInitialized] = useState(false);

  // Local variables
  const subdomain = useWatch({ control: form.control, name: 'subdomain' });
  const selectedLanguage = useWatch({ control: form.control, name: 'language' });
  const showButtons = subdomain && selectedLanguage && selectedCategoryIds.length > 0;

  const showKeepPublished =
    isZendeskEditing && !datasource?.is_draft && datasource?.status === 'available';

  // Sync form values with local state
  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === 'categories' || name === undefined) {
        setSelectedCategoryIds(value.categories || []);
      }
    });

    return () => subscription.unsubscribe();
  }, [form]);

  // Handlers
  const resetZendeskForm = useCallback(() => {
    form.unregister('language');
    form.unregister('categories');
    setZendeskLanguages([]);
    setZendeskCategories([]);
  }, [form]);

  const handleSelectAllCategories = (checked: boolean) => {
    const newCategories = checked ? zendeskCategories.map((cat) => cat.id) : [];

    setSelectedCategoryIds(newCategories);
    form.setValue('categories', newCategories, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  };

  const fetchZendeskLocales = useCallback(
    async (domainOverride?: string) => {
      const domain = domainOverride ?? form.getValues('subdomain');

      if (!domain) {
        return false;
      }

      const encodedInfo = encodeBase64(`${ZENDESK_EMAIL}/token:${ZENDESK_ACCESS_TOKEN}`);

      setIsLanguageLoading(true);

      try {
        // Validate the subdomain
        const zendeskLocales = await callPost(urlValidationEndpoints.locales(domain), {
          encodedInfo,
        });

        const candidateLocales: string[] = zendeskLocales.locales.filter((locale: string) =>
          locale.includes(collection?.language)
        );

        const articleCounts: LocaleArticleCount[] = await Promise.all(
          candidateLocales.map(async (locale) => {
            try {
              const response = await callGet(urlValidationEndpoints.articles(domain, locale));
              return { locale, count: response.pagination?.count || 0 };
            } catch {
              return { locale, count: 0 };
            }
          })
        );

        const filteredLanguages = articleCounts
          .filter(({ count }) => count > 0)
          .map(({ locale }) => ({ label: t([locale], { ns: 'languages' }), value: locale }));

        setZendeskLanguages(filteredLanguages);

        // Clear subdomain error in case it was set before in a previous failed attempt
        form.clearErrors('subdomain');
        return true;
      } catch {
        form.setError('subdomain', { message: t('collections.locale_error') });
        return false;
      } finally {
        setIsLanguageLoading(false);
      }
    },
    [collection?.language, form, t]
  );

  const fetchZendeskCategories = useCallback(
    async (subdomainOverride?: string, localeOverride?: string) => {
      const locale = localeOverride ?? form.getValues('language');
      const subdomain = subdomainOverride ?? form.getValues('subdomain');

      if (!locale || !subdomain) {
        return false;
      }

      setIsCategoriesLoading(true);
      try {
        const response = await callGet(urlValidationEndpoints.categories(subdomain, locale));

        setZendeskCategories(response.categories || []);
        const newCategories = response.categories.map((cat) => cat.id);
        setSelectedCategoryIds(newCategories);
        form.setValue('categories', newCategories, { shouldDirty: !isZendeskEditing });
        return true;
      } catch {
        form.setError('language', { message: t('collections.locale_error') });
        return false;
      } finally {
        setIsCategoriesLoading(false);
      }
    },
    [form, t, isZendeskEditing]
  );

  const handleDomainChange = async () => {
    await fetchZendeskLocales();
  };

  // Submit is only triggered for Zendesk datasource
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    // Prevent the form from being submitted
    e.preventDefault();

    const { subdomain, categories, language, keep_published } = form.getValues();

    // Create a base name for the Zendesk datasource
    const baseName = `${t('integrations.zendesk.name')} ${subdomain}`;

    // Use the generateUniqueName utility to create a unique name
    const datasourceName = isZendeskEditing
      ? datasource.name || baseName
      : generateUniqueName(baseName, extractNames(datasources || []));

    const newDatasource: Partial<Datasource> = {
      type: DatasourceType.ZENDESK_KB,
      name: datasourceName,
      config: {
        access_token: ZENDESK_ACCESS_TOKEN,
        email: ZENDESK_EMAIL,
        subdomain,
        locales: [language],
        categories: categories || [],
      },
    };

    if (isZendeskEditing) {
      updateDatasource(
        {
          datasource_id: datasource.datasource_id,
          collection_id: datasource.collection_id,
          ...newDatasource,
          is_draft: !keep_published,
        },
        {
          onSuccess: () => {
            toast.success(t('collections.toast_messages.external_source_updated'));
            handleCancel();
          },
        }
      );
    } else {
      createDatasource(newDatasource, {
        onSuccess: () => {
          toast(t('collections.toast_messages.external_source_uploaded'), {
            description: t('collections.toast_messages.external_source_draft'),

            action: {
              label: t('collections.publish'),
              onClick: () => {
                dispatch(openPublishModal());
              },
            },
          });

          handleCancel();
        },
        onError: () => {
          toast(t('collections.toast_messages.external_source_not_connected'), {
            action: { onClick: noop, label: t('collections.publish') },
          });

          toast.custom((id) => (
            <div
              className={cn(
                'flex w-full max-w-md items-center justify-between rounded-lg border border-gray-100 bg-background p-6 shadow-md animate-in fade-in slide-in-from-top-full'
              )}
              role="alert"
            >
              <div className="flex-1">
                <h2 className="text-sm leading-5 font-semibold">
                  {t('collections.toast_messages.external_source_not_connected')}
                </h2>
              </div>

              <Button
                size="sm"
                variant="outline-neutral"
                onClick={() => {
                  toast.dismiss(id);
                }}
              >
                {t('common.try_again')}
              </Button>
            </div>
          ));
        },
      });
    }
  };

  // Load datasource data only once when editing an existing Zendesk datasource
  useEffect(() => {
    async function initializeEditMode() {
      if (
        initializedRef.current ||
        !isZendeskEditing ||
        !collection?.language ||
        !isZendeskConfig(datasource.config)
      ) {
        return;
      }
      initializedRef.current = true;

      const { subdomain, locales, categories } = datasource.config;
      const locale = locales?.[0];

      form.setValue('subdomain', subdomain, { shouldDirty: false });

      await fetchZendeskLocales(subdomain);
      if (locale) {
        form.setValue('language', locale, { shouldDirty: false });
      }

      await fetchZendeskCategories(subdomain, locale);
      if (categories?.length) {
        form.setValue('categories', categories, { shouldDirty: false });
        setSelectedCategoryIds(categories);
      }

      form.setValue('keep_published', true);
      setIsEditInitialized(true);
    }

    initializeEditMode();
  }, [
    isZendeskEditing,
    fetchZendeskLocales,
    fetchZendeskCategories,
    form,
    datasource,
    collection?.language,
  ]);

  return (
    <>
      <SidebarWrapperContent className={EXTERNAL_SOURCE_SECTION}>
        <FormField
          control={form.control}
          name="subdomain"
          render={({ field }) => (
            <FormItem className={EXTERNAL_SOURCE_FIELD}>
              <FormLabel>{t('integrations.zendesk.auth.subdomain')}</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  onChange={(e) => {
                    field.onChange(e.target.value.trim());
                    // Reset the form when the subdomain changes
                    resetZendeskForm();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                    }
                  }}
                  onKeyUp={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();

                      // Blur the input to trigger the onBlur event
                      (e.currentTarget as HTMLInputElement).blur();
                    }
                  }}
                  onBlur={() => {
                    handleDomainChange();
                  }}
                  placeholder={t('integrations.zendesk.auth.subdomain_placeholder')}
                  readOnly={!canWrite}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="language"
          render={({ field }) => (
            <FormItem className={EXTERNAL_SOURCE_FIELD}>
              <FormLabel>{t('collections.add_external_sources.select_language')}</FormLabel>
              <Select
                onValueChange={async (value) => {
                  if (!value) {
                    return;
                  }
                  // Re-selecting the current locale re-checks every category, so only retry when none loaded.
                  if (value === field.value && zendeskCategories.length > 0) {
                    return;
                  }
                  field.onChange(value);
                  await fetchZendeskCategories();
                }}
                value={field.value || undefined}
                disabled={!canWrite || isLanguageLoading || zendeskLanguages.length === 0}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder={t('common.select')} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {zendeskLanguages.map((language) => {
                    const [, region] = language.value.split('-');

                    return (
                      <SelectItem key={language.value} value={language.value}>
                        <div className="flex size-4 items-center justify-center">
                          <TemplateFlagIcon
                            countryCode={region ?? getCountryCodeFromLanguage(language.value)}
                          />
                        </div>

                        {language.label}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="categories"
          render={() => (
            <FormItem className={EXTERNAL_SOURCE_CATEGORIES}>
              <div className={cn(EXTERNAL_SOURCE_FIELD, 'gap-0')}>
                <FormLabel>
                  {t('collections.add_external_sources.select_article_categories_title')}
                </FormLabel>
                <p className="text-xs leading-5 text-foreground-muted">
                  {t('collections.add_external_sources.select_article_categories_description')}
                </p>
              </div>
              {isCategoriesLoading || (!!datasourceId && !isEditInitialized) ? (
                <CategorySkeleton />
              ) : (
                <div className="max-h-70 w-full space-y-2 overflow-y-auto py-1 pl-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="select-all"
                      checked={
                        zendeskCategories.length > 0 &&
                        selectedCategoryIds.length === zendeskCategories.length
                      }
                      onCheckedChange={(checked) => handleSelectAllCategories(!!checked)}
                      disabled={!canWrite || zendeskCategories.length === 0}
                    />
                    <label
                      htmlFor="select-all"
                      className="text-sm leading-none font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      {t('collections.add_external_sources.select_all')} ({zendeskCategories.length}
                      )
                    </label>
                  </div>
                  <Separator />

                  {zendeskCategories.map((category) => (
                    <div key={category.id} className="my-4 flex items-center space-x-2">
                      <Checkbox
                        id={`category-${category.id}`}
                        checked={selectedCategoryIds.includes(category.id)}
                        onCheckedChange={(checked) => {
                          let newCategories: string[];

                          // Select category
                          if (checked) {
                            newCategories = [...selectedCategoryIds, category.id];
                          }
                          // Unselect category
                          else {
                            newCategories = selectedCategoryIds.filter(
                              (catId) => catId !== category.id
                            );
                          }

                          setSelectedCategoryIds(newCategories);
                          form.setValue('categories', newCategories, {
                            shouldDirty: true,
                            shouldTouch: true,
                            shouldValidate: true,
                          });
                        }}
                        disabled={!canWrite}
                      />
                      <label
                        htmlFor={`category-${category.id}`}
                        className="text-sm leading-none font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        {category.name}
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </FormItem>
          )}
        />

        {showKeepPublished && (
          <FormField
            control={form.control}
            name="keep_published"
            render={({ field }) => (
              <FormItem className="flex gap-2 space-y-0 rounded-md border p-4">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    disabled={!canWrite}
                  />
                </FormControl>

                <FormLabel className="flex flex-col items-start gap-1 leading-none">
                  {t('collections.add_faq.keep_published')}

                  <FormDescription>
                    {t('collections.add_faq.keep_published_description')}
                  </FormDescription>
                </FormLabel>
              </FormItem>
            )}
          />
        )}
      </SidebarWrapperContent>

      {showButtons && (
        <SidebarWrapperFooter className={EXTERNAL_SOURCE_FOOTER}>
          <Button type="button" variant="ghost-neutral" onClick={() => handleCancel(true)}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleClick} type="submit">
            {isZendeskEditing ? t('common.update') : t('common.add')}
          </Button>
        </SidebarWrapperFooter>
      )}
    </>
  );
};
